import type { RowSortPin } from "./types";

/**
 * Sorts `rows` with `sort`, leaving out the rows `getPin` pins and placing them back by their pins (`RowSortPin`): an
 * `after` row right under its anchor (and its own `after` rows under it), then the `last` rows, each followed by its
 * own `after` rows. Pinned rows keep their arrival order among themselves; an `after` row whose anchor isn't in `rows`
 * counts as `last`. Pure.
 *
 * Every row comes back exactly once. A pin cycle (two rows each under the other) leaves rows with nothing to follow,
 * which is a caller bug, and throws.
 */
export const sortRowsWithPins = <T>(
	rows: readonly T[],
	sort: (rows: T[]) => T[],
	getRowId: (row: T) => string | number,
	getPin: (row: T) => RowSortPin | null,
): T[] => {
	const ids = new Set(rows.map(getRowId));
	const free: T[] = [];
	const last: T[] = [];
	const followers = new Map<string | number, T[]>();
	for (const row of rows) {
		const pin = getPin(row);
		if (pin === null) free.push(row);
		else if (pin.kind === "after" && ids.has(pin.rowId)) {
			const list = followers.get(pin.rowId);
			if (list === undefined) followers.set(pin.rowId, [row]);
			else list.push(row);
		} else last.push(row);
	}
	const out: T[] = [];
	const emit = (row: T): void => {
		out.push(row);
		// DEFAULT: [] — a row nothing is pinned under.
		for (const follower of followers.get(getRowId(row)) ?? []) emit(follower);
	};
	for (const row of sort(free)) emit(row);
	for (const row of last) emit(row);
	if (out.length !== rows.length) {
		throw new Error(`SystemDataGrid: getRowSortPin placed ${out.length} of ${rows.length} rows; a row is pinned under a row pinned under it`);
	}
	return out;
};
